/**
 * Outreach Types admin — configure engagement-type and disposition-type dropdown
 * lists per program + MCP (payer). Edits the outreach_type_config rows the outreach
 * composer reads.
 */
import { useTabbedChrome } from '@/components/weavein';
import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Save } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import outreachBillingApi, { OutreachTypeConfig, TypeOption } from '@/services/api/outreachBilling';

export default function OutreachTypesPage() {
  const tabbed = useTabbedChrome();
  const [configs, setConfigs] = useState<OutreachTypeConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setConfigs(await outreachBillingApi.getTypeConfigs());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (idx: number, patch: Partial<OutreachTypeConfig>) =>
    setConfigs((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  const editList = (
    idx: number,
    key: 'engagement_types' | 'disposition_types',
    j: number,
    field: 'value' | 'label',
    val: string,
  ) =>
    update(idx, {
      [key]: configs[idx][key].map((o, k) => (k === j ? { ...o, [field]: val } : o)),
    } as any);

  const addRow = (idx: number, key: 'engagement_types' | 'disposition_types') =>
    update(idx, { [key]: [...configs[idx][key], { value: '', label: '' }] } as any);

  const removeRow = (idx: number, key: 'engagement_types' | 'disposition_types', j: number) =>
    update(idx, { [key]: configs[idx][key].filter((_, k) => k !== j) } as any);

  const save = (c: OutreachTypeConfig) =>
    (async () => {
      setBusy(c.id);
      setStatus(null);
      setError(null);
      try {
        await outreachBillingApi.saveTypeConfig({
          program_id: c.program_id,
          payer_id: c.payer_id,
          engagement_types: c.engagement_types.filter((o) => o.value),
          disposition_types: c.disposition_types.filter((o) => o.value),
        });
        setStatus('Saved.');
        await load();
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Save failed');
      } finally {
        setBusy(null);
      }
    })();

  const listEditor = (c: OutreachTypeConfig, idx: number, key: 'engagement_types' | 'disposition_types', title: string) => (
    <div className="flex-1 min-w-[240px]">
      <div className="text-xs font-medium text-gray-500 uppercase mb-1">{title}</div>
      <div className="space-y-1">
        {c[key].map((o, j) => (
          <div key={j} className="flex items-center gap-1">
            <input
              className="border rounded px-2 py-1 text-xs w-28 bg-white dark:bg-gray-800"
              value={o.value}
              placeholder="value"
              onChange={(e) => editList(idx, key, j, 'value', e.target.value)}
            />
            <input
              className="border rounded px-2 py-1 text-xs flex-1 bg-white dark:bg-gray-800"
              value={o.label}
              placeholder="Label"
              onChange={(e) => editList(idx, key, j, 'label', e.target.value)}
            />
            <button onClick={() => removeRow(idx, key, j)} className="text-gray-400 hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button onClick={() => addRow(idx, key)} className="text-xs text-teal-600 flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <div>
        {!tabbed && <h1 className="font-display text-lg font-semibold tracking-[-0.015em] text-ink">Outreach Types</h1>}
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Configure engagement and disposition dropdowns per program and MCP. The outreach composer reads these.
        </p>
      </div>

      {(status || error) && (
        <div className={`text-sm rounded-md px-3 py-2 ${error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
          {error || status}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : configs.length === 0 ? (
        <div className="text-sm text-gray-500">No outreach type config found for this org.</div>
      ) : (
        configs.map((c, idx) => (
          <Card key={c.id}>
            <CardHeader>
              <CardTitle className="text-base">
                {c.program_name || 'All programs'} · {c.payer_name || 'All payers (default)'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-6">
                {listEditor(c, idx, 'engagement_types', 'Engagement types')}
                {listEditor(c, idx, 'disposition_types', 'Disposition types')}
              </div>
              <div className="mt-3">
                <Button size="sm" onClick={save(c)} disabled={busy === c.id}>
                  {busy === c.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
